class YhSizeChart extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.modal = this.querySelector('[data-size-chart-modal]');
    if (!(this.modal instanceof HTMLDialogElement)) return;
    this.cards = [...this.modal.querySelectorAll('[data-card-index]')];
    this.panels = [...this.modal.querySelectorAll('[data-panel-index]')];
    this.carousel = this.modal.querySelector('[data-size-chart-carousel]');
    document.addEventListener('click', (event) => {
      const trigger = event.target instanceof Element ? event.target.closest('[data-size-chart-open]') : null;
      if (!trigger || (trigger.dataset.sizeChartProduct && trigger.dataset.sizeChartProduct !== this.dataset.productId)) return;
      this.open(trigger);
    }, { signal });
    this.modal.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      if (event.target.closest('[data-size-chart-close]') || event.target === this.modal) {
        this.modal.close();
        return;
      }
      const card = event.target.closest('[data-card-index]');
      if (card) this.activate(Number(card.dataset.cardIndex), true);
    }, { signal });
    this.modal.addEventListener('close', () => {
      if (this.scroller) this.scroller.style.overflow = this.previousOverflow;
      this.append(this.modal);
      if (this.trigger?.isConnected) this.trigger.focus({ preventScroll: true });
    }, { signal });
    this.modal.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab') return;
      const controls = [...this.modal.querySelectorAll('button, a[href], input, select, textarea, [tabindex]')]
        .filter((element) => !element.disabled && element.tabIndex >= 0 && element.getClientRects().length);
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (!first) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }, { signal });
    this.carousel?.addEventListener('scroll', () => {
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => {
        const bounds = this.carousel.getBoundingClientRect();
        const center = bounds.left + bounds.width / 2;
        const nearest = this.cards.reduce((best, card) => {
          const rect = card.getBoundingClientRect();
          const distance = Math.abs(rect.left + rect.width / 2 - center);
          return !best || distance < best.distance ? { card, distance } : best;
        }, null);
        if (nearest) this.activate(Number(nearest.card.dataset.cardIndex));
      }, 100);
    }, { signal, passive: true });
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
    clearTimeout(this.scrollTimer);
    if (this.modal?.open) this.modal.close();
    if (this.scroller) this.scroller.style.overflow = this.previousOverflow;
    if (this.modal?.parentElement === document.body) this.modal.remove();
  }

  activate(index, scroll = false) {
    for (const [i, card] of this.cards.entries()) {
      card.classList.toggle('is-active', i === index);
      card.setAttribute('aria-pressed', String(i === index));
    }
    for (const [i, panel] of this.panels.entries()) panel.classList.toggle('is-active', i === index);
    if (scroll && this.cards[index] && this.carousel) {
      this.carousel.scrollTo({
        left: this.cards[index].offsetLeft - (this.carousel.clientWidth - this.cards[index].offsetWidth) / 2,
        behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
      });
    }
  }

  open(trigger) {
    if (this.modal.open) return;
    this.trigger = trigger;
    this.scroller = document.querySelector('.page-wrapper');
    this.previousOverflow = this.scroller?.style.overflow || '';
    if (this.scroller) this.scroller.style.overflow = 'hidden';
    document.body.append(this.modal);
    this.modal.showModal();
    const index = this.cards.findIndex((card) => card.dataset.chartName === this.dataset.initialChart);
    this.activate(Math.max(0, index));
    requestAnimationFrame(() => this.activate(Math.max(0, index), true));
    this.modal.querySelector('[data-size-chart-close].size-chart__close')?.focus();
  }
}
if (!customElements.get('yh-size-chart')) customElements.define('yh-size-chart', YhSizeChart);
